/**
 * LocalPartyLobby - Lobby UI for Local Party Mode
 *
 * Provides host and client views for configuring and joining local party games.
 * Supports preset selection, custom configuration, player list management, and ready state.
 */

import {
  LocalPartyConfig,
  LocalGameMode,
  TeamMode,
  MapShape,
  ShrinkSpeed,
  FoodDensity,
  AIDifficulty,
  PowerupRatePresetId,
  VisibilityPresetId,
  CONFIG_LIMITS,
  PRESET_QUICK_SURVIVAL,
  PRESET_TEAM_BATTLE,
  PRESET_PARTY_CHAOS,
  PRESET_PRACTICE,
  PRESET_COMPETITIVE,
  getDefaultConfig,
  getGameModeDisplayName,
  getTeamModeDisplayName,
  getPowerupRateDisplayName,
  getVisibilityDisplayName,
  getAIDifficultyDisplayName,
} from '../local-party/LocalPartyConfig';
import { SoundManager, SoundType } from '../audio/SoundManager';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Player information for the player list
 */
export interface PlayerInfo {
  id: string;
  name: string;
  isHost: boolean;
  isReady: boolean;
  isAI: boolean;
  team?: number;
}

/**
 * Event handlers for LocalPartyLobby
 */
export interface LocalPartyLobbyEvents {
  onHostGame: () => void;
  onJoinGame: (roomCode: string) => void;
  onLeave: () => void;
  onStartGame: () => void;
  onConfigChange: (config: LocalPartyConfig) => void;
  onReadyChange: (ready: boolean) => void;
  onBack: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** UI Colors matching ConnectionUI */
const COLORS = {
  primary: '#4ecdc4',
  accent: '#ff6b6b',
  bgDark: 'rgba(25, 25, 40, 0.65)',
  bgDarker: 'rgba(0, 0, 0, 0.3)',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.15)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.6)',
  textMuted: 'rgba(255, 255, 255, 0.4)',
  success: '#4ecdc4',
  warning: '#f9ed69',
  error: '#ff6b6b',
} as const;

/** Preset configurations with display names */
const PRESETS: { key: string; name: string; config: LocalPartyConfig }[] = [
  { key: 'quick_survival', name: 'Quick Survival', config: PRESET_QUICK_SURVIVAL },
  { key: 'team_battle', name: 'Team Battle', config: PRESET_TEAM_BATTLE },
  { key: 'party_chaos', name: 'Party', config: PRESET_PARTY_CHAOS },
  { key: 'competitive', name: 'Competitive', config: PRESET_COMPETITIVE },
  { key: 'practice', name: 'Practice', config: PRESET_PRACTICE },
];

// ============================================================================
// LOCALPARTYLOBBY CLASS
// ============================================================================

/**
 * LocalPartyLobby - UI component for local party game lobby
 */
export class LocalPartyLobby {
  /** Main container element */
  private _container: HTMLDivElement;

  /** Event handlers */
  private _events: LocalPartyLobbyEvents;

  /** Current configuration */
  private _config: LocalPartyConfig;

  /** Whether this client is the host */
  private _isHost: boolean = false;

  /** Room code display element */
  private _roomCodeDisplay: HTMLDivElement | null = null;

  /** Player list container */
  private _playerListContainer: HTMLDivElement | null = null;

  /** Start game button */
  private _startButton: HTMLButtonElement | null = null;

  /** Join room code input */
  private _roomCodeInput: HTMLInputElement | null = null;

  /** Selected preset key */
  private _selectedPreset: string = 'quick_survival';

  /** Preset buttons map */
  private _presetButtons: Map<string, HTMLButtonElement> = new Map();

  /** Config control elements for updating */
  private _configControls: Map<string, HTMLSelectElement | HTMLInputElement> = new Map();

  constructor(events: LocalPartyLobbyEvents) {
    this._events = events;
    this._config = getDefaultConfig();
    this._container = this._createContainer();
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Show the host view with room code
   */
  public showHostView(roomCode: string): void {
    this._isHost = true;
    this._container.innerHTML = '';
    this._container.appendChild(this._createHostView(roomCode));
    this._container.style.display = 'flex';
  }

  /**
   * Show the join view for entering room code
   */
  public showJoinView(): void {
    this._isHost = false;
    this._container.innerHTML = '';
    this._container.appendChild(this._createJoinView());
    this._container.style.display = 'flex';
  }

  /**
   * Hide the lobby
   */
  public hide(): void {
    this._container.style.display = 'none';
  }

  /**
   * Update the player list display
   */
  public updatePlayerList(players: PlayerInfo[]): void {
    if (!this._playerListContainer) return;

    this._playerListContainer.innerHTML = '';

    const maxPlayers = this._config.maxPlayers;
    const humanPlayers = players.filter((p) => !p.isAI);
    const aiPlayerCount = players.filter((p) => p.isAI).length;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid ${COLORS.border};
    `;

    const title = document.createElement('span');
    title.textContent = 'PLAYERS';
    title.style.cssText = `
      font-size: 11px;
      font-weight: 700;
      color: ${COLORS.textMuted};
      letter-spacing: 1.5px;
    `;
    header.appendChild(title);

    const countText = aiPlayerCount > 0
      ? `${humanPlayers.length}/${maxPlayers} + ${aiPlayerCount} AI`
      : `${humanPlayers.length}/${maxPlayers}`;
    const count = document.createElement('span');
    count.textContent = countText;
    count.style.cssText = `
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.primary};
    `;
    header.appendChild(count);

    this._playerListContainer.appendChild(header);

    // Player entries
    const list = document.createElement('div');
    list.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 8px;
      max-height: 200px;
      overflow-y: auto;
    `;

    players.forEach((player) => {
      const entry = this._createPlayerEntry(player);
      list.appendChild(entry);
    });

    this._playerListContainer.appendChild(list);

    // Update start button state if host
    this._updateStartButtonState(players);
  }

  /**
   * Update the configuration display
   */
  public updateConfig(config: LocalPartyConfig): void {
    this._config = { ...config };

    // Update all config controls
    this._configControls.forEach((control, key) => {
      const value = this._getConfigValue(config, key);
      if (value !== undefined) {
        if (control instanceof HTMLSelectElement) {
          control.value = String(value);
        } else if (control instanceof HTMLInputElement) {
          if (control.type === 'checkbox') {
            control.checked = Boolean(value);
          } else {
            control.value = String(value);
          }
        }
      }
    });

    // Update preset selection
    this._updatePresetSelection();
  }

  /**
   * Set whether the start button should be enabled
   */
  public setCanStart(canStart: boolean): void {
    if (this._startButton) {
      this._startButton.disabled = !canStart;
      this._startButton.style.opacity = canStart ? '1' : '0.5';
      this._startButton.style.cursor = canStart ? 'pointer' : 'not-allowed';
    }
  }

  /**
   * Get the container element
   */
  public getElement(): HTMLDivElement {
    return this._container;
  }

  /**
   * Destroy the component
   */
  public destroy(): void {
    this._container.remove();
    this._presetButtons.clear();
    this._configControls.clear();
  }

  // ============================================================================
  // PRIVATE METHODS - UI CREATION
  // ============================================================================

  /**
   * Create the main container
   */
  private _createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'local-party-lobby';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at center, rgba(10, 10, 26, 0.85), rgba(5, 5, 15, 0.98));
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      z-index: 1000;
      font-family: 'Segoe UI', Arial, sans-serif;
      overflow-y: auto;
      padding: 20px;
      box-sizing: border-box;
    `;
    document.body.appendChild(container);
    return container;
  }

  /**
   * Create the host view
   */
  private _createHostView(roomCode: string): HTMLDivElement {
    const card = this._createCard();

    // Header with room code
    card.appendChild(this._createHeader('LOCAL PARTY', roomCode));

    // Preset buttons
    card.appendChild(this._createPresetSection());

    // Game settings
    card.appendChild(this._createSettingsSection(true));

    // Player list
    this._playerListContainer = this._createPlayerListSection();
    card.appendChild(this._playerListContainer);

    // Action buttons
    card.appendChild(this._createHostActionButtons());

    return card;
  }

  /**
   * Create the join view
   */
  private _createJoinView(): HTMLDivElement {
    const card = this._createCard();

    // Header
    const header = document.createElement('div');
    header.style.cssText = `text-align: center; margin-bottom: 24px;`;

    const title = document.createElement('h1');
    title.textContent = '🎉 LOCAL PARTY';
    title.style.cssText = `
      font-size: 32px;
      font-weight: 900;
      letter-spacing: -1px;
      margin: 0;
      background: linear-gradient(135deg, ${COLORS.primary} 0%, #a78bfa 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    `;
    header.appendChild(title);

    const subtitle = document.createElement('p');
    subtitle.textContent = 'Play with friends on your local network';
    subtitle.style.cssText = `
      font-size: 14px;
      color: ${COLORS.textSecondary};
      margin: 8px 0 0 0;
    `;
    header.appendChild(subtitle);
    card.appendChild(header);

    // Host a Game button (big and prominent)
    const hostButton = document.createElement('button');
    hostButton.innerHTML = '🏠 Host a Game';
    hostButton.style.cssText = `
      width: 100%;
      padding: 18px 24px;
      font-size: 18px;
      font-weight: 700;
      background: linear-gradient(135deg, ${COLORS.primary} 0%, #a78bfa 100%);
      border: none;
      border-radius: 14px;
      color: white;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 20px;
    `;
    hostButton.addEventListener('mouseover', () => {
      hostButton.style.transform = 'scale(1.02)';
      hostButton.style.boxShadow = `0 8px 25px rgba(78, 205, 196, 0.4)`;
    });
    hostButton.addEventListener('mouseout', () => {
      hostButton.style.transform = 'scale(1)';
      hostButton.style.boxShadow = 'none';
    });
    hostButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._events.onHostGame();
    });
    card.appendChild(hostButton);

    // Divider with "OR"
    const divider = document.createElement('div');
    divider.style.cssText = `
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
    `;
    const line1 = document.createElement('div');
    line1.style.cssText = `flex: 1; height: 1px; background: ${COLORS.border};`;
    const orText = document.createElement('span');
    orText.textContent = 'OR';
    orText.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: ${COLORS.textMuted};
      letter-spacing: 1px;
    `;
    const line2 = document.createElement('div');
    line2.style.cssText = `flex: 1; height: 1px; background: ${COLORS.border};`;
    divider.appendChild(line1);
    divider.appendChild(orText);
    divider.appendChild(line2);
    card.appendChild(divider);

    // Join section
    const joinSection = document.createElement('div');
    joinSection.style.cssText = `margin-bottom: 16px;`;

    const joinLabel = document.createElement('label');
    joinLabel.textContent = 'JOIN A GAME';
    joinLabel.style.cssText = `
      display: block;
      font-size: 11px;
      font-weight: 700;
      color: ${COLORS.textMuted};
      margin-bottom: 8px;
      letter-spacing: 1.5px;
    `;
    joinSection.appendChild(joinLabel);

    this._roomCodeInput = document.createElement('input');
    this._roomCodeInput.type = 'text';
    this._roomCodeInput.placeholder = 'Enter room code';
    this._roomCodeInput.maxLength = 6;
    this._roomCodeInput.style.cssText = `
      width: 100%;
      padding: 14px 20px;
      font-size: 20px;
      font-weight: 700;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 6px;
      background: ${COLORS.bgDarker};
      border: 2px solid ${COLORS.border};
      border-radius: 14px;
      color: ${COLORS.textPrimary};
      outline: none;
      transition: all 0.2s ease;
      box-sizing: border-box;
    `;
    this._roomCodeInput.addEventListener('focus', () => {
      this._roomCodeInput!.style.borderColor = COLORS.primary;
    });
    this._roomCodeInput.addEventListener('blur', () => {
      this._roomCodeInput!.style.borderColor = COLORS.border;
    });
    this._roomCodeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this._handleJoin();
      }
    });
    joinSection.appendChild(this._roomCodeInput);
    card.appendChild(joinSection);

    // Action buttons
    const buttonRow = document.createElement('div');
    buttonRow.style.cssText = `display: flex; gap: 12px;`;

    const joinButton = this._createPrimaryButton('Join Game', () => this._handleJoin());
    joinButton.style.flex = '1';
    buttonRow.appendChild(joinButton);

    const backButton = this._createSecondaryButton('Back', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._events.onBack();
    });
    buttonRow.appendChild(backButton);

    card.appendChild(buttonRow);

    return card;
  }

  /**
   * Create the card container
   */
  private _createCard(): HTMLDivElement {
    const card = document.createElement('div');
    card.style.cssText = `
      width: 100%;
      max-width: 600px;
      background: ${COLORS.bgDark};
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid ${COLORS.border};
      border-radius: 24px;
      padding: 28px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      gap: 20px;
    `;
    return card;
  }

  /**
   * Create the header section with room code
   */
  private _createHeader(title: string, roomCode?: string): HTMLDivElement {
    const header = document.createElement('div');
    header.style.cssText = `
      text-align: center;
      padding-bottom: 16px;
      border-bottom: 1px solid ${COLORS.border};
    `;

    const titleEl = document.createElement('h1');
    titleEl.textContent = title;
    titleEl.style.cssText = `
      font-size: 28px;
      font-weight: 900;
      letter-spacing: -1px;
      margin: 0 0 12px 0;
      background: linear-gradient(135deg, ${COLORS.primary} 0%, #a78bfa 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    `;
    header.appendChild(titleEl);

    if (roomCode) {
      this._roomCodeDisplay = document.createElement('div');
      this._roomCodeDisplay.style.cssText = `
        display: inline-flex;
        align-items: center;
        gap: 12px;
        background: ${COLORS.bgDarker};
        padding: 12px 20px;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid ${COLORS.border};
      `;

      const codeLabel = document.createElement('span');
      codeLabel.textContent = 'Room Code:';
      codeLabel.style.cssText = `
        font-size: 13px;
        color: ${COLORS.textSecondary};
      `;
      this._roomCodeDisplay.appendChild(codeLabel);

      const codeValue = document.createElement('span');
      codeValue.textContent = roomCode;
      codeValue.style.cssText = `
        font-size: 20px;
        font-weight: 800;
        color: ${COLORS.primary};
        letter-spacing: 4px;
      `;
      this._roomCodeDisplay.appendChild(codeValue);

      const copyIcon = document.createElement('span');
      copyIcon.textContent = 'Copy';
      copyIcon.style.cssText = `
        font-size: 11px;
        color: ${COLORS.textMuted};
        padding: 4px 8px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 6px;
      `;
      this._roomCodeDisplay.appendChild(copyIcon);

      this._roomCodeDisplay.addEventListener('click', () => {
        navigator.clipboard.writeText(roomCode);
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        copyIcon.textContent = 'Copied!';
        copyIcon.style.color = COLORS.success;
        setTimeout(() => {
          copyIcon.textContent = 'Copy';
          copyIcon.style.color = COLORS.textMuted;
        }, 2000);
      });

      this._roomCodeDisplay.addEventListener('mouseover', () => {
        this._roomCodeDisplay!.style.borderColor = COLORS.borderHover;
        SoundManager.getInstance().play(SoundType.UI_HOVER);
      });
      this._roomCodeDisplay.addEventListener('mouseout', () => {
        this._roomCodeDisplay!.style.borderColor = COLORS.border;
      });

      header.appendChild(this._roomCodeDisplay);
    }

    return header;
  }

  /**
   * Create the preset selection section
   */
  private _createPresetSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.style.cssText = `
      background: ${COLORS.bgDarker};
      border-radius: 16px;
      padding: 16px;
      border: 1px solid ${COLORS.border};
    `;

    const label = document.createElement('div');
    label.textContent = 'PRESETS';
    label.style.cssText = `
      font-size: 11px;
      font-weight: 700;
      color: ${COLORS.textMuted};
      letter-spacing: 1.5px;
      margin-bottom: 12px;
    `;
    section.appendChild(label);

    const buttonGrid = document.createElement('div');
    buttonGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    `;

    PRESETS.forEach((preset) => {
      const btn = document.createElement('button');
      btn.textContent = preset.name;
      btn.style.cssText = `
        padding: 10px 12px;
        border: 1px solid ${COLORS.border};
        border-radius: 10px;
        background: transparent;
        color: ${COLORS.textSecondary};
        font-weight: 600;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
      `;

      btn.addEventListener('click', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        this._selectPreset(preset.key);
      });
      btn.addEventListener('mouseover', () => {
        if (this._selectedPreset !== preset.key) {
          btn.style.background = 'rgba(78, 205, 196, 0.1)';
          btn.style.borderColor = COLORS.borderHover;
        }
        SoundManager.getInstance().play(SoundType.UI_HOVER);
      });
      btn.addEventListener('mouseout', () => {
        if (this._selectedPreset !== preset.key) {
          btn.style.background = 'transparent';
          btn.style.borderColor = COLORS.border;
        }
      });

      this._presetButtons.set(preset.key, btn);
      buttonGrid.appendChild(btn);
    });

    // Add Custom button
    const customBtn = document.createElement('button');
    customBtn.textContent = 'Custom';
    customBtn.style.cssText = `
      padding: 10px 12px;
      border: 1px solid ${COLORS.border};
      border-radius: 10px;
      background: transparent;
      color: ${COLORS.textSecondary};
      font-weight: 600;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    customBtn.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._selectPreset('custom');
    });
    customBtn.addEventListener('mouseover', () => {
      if (this._selectedPreset !== 'custom') {
        customBtn.style.background = 'rgba(78, 205, 196, 0.1)';
      }
      SoundManager.getInstance().play(SoundType.UI_HOVER);
    });
    customBtn.addEventListener('mouseout', () => {
      if (this._selectedPreset !== 'custom') {
        customBtn.style.background = 'transparent';
      }
    });
    this._presetButtons.set('custom', customBtn);
    buttonGrid.appendChild(customBtn);

    section.appendChild(buttonGrid);

    // Set initial preset selection
    this._updatePresetButtonStyles();

    return section;
  }

  /**
   * Create the game settings section with all settings organized into collapsible groups
   */
  private _createSettingsSection(editable: boolean): HTMLDivElement {
    const section = document.createElement('div');
    section.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;

    // === GAME RULES SECTION ===
    section.appendChild(this._createSettingsGroup('GAME RULES', editable, (grid) => {
      // Game Mode
      grid.appendChild(
        this._createSelectControl('gameMode', 'Mode', [
          { value: LocalGameMode.SURVIVAL, label: getGameModeDisplayName(LocalGameMode.SURVIVAL) },
          { value: LocalGameMode.SCORE_RACE, label: getGameModeDisplayName(LocalGameMode.SCORE_RACE) },
          { value: LocalGameMode.TIME_ATTACK, label: getGameModeDisplayName(LocalGameMode.TIME_ATTACK) },
          { value: LocalGameMode.KING_OF_WORMS, label: getGameModeDisplayName(LocalGameMode.KING_OF_WORMS) },
        ], editable)
      );

      // Team Mode
      grid.appendChild(
        this._createSelectControl('teamMode', 'Teams', [
          { value: TeamMode.FREE_FOR_ALL, label: getTeamModeDisplayName(TeamMode.FREE_FOR_ALL) },
          { value: TeamMode.TEAMS, label: getTeamModeDisplayName(TeamMode.TEAMS) },
          { value: TeamMode.DUOS, label: getTeamModeDisplayName(TeamMode.DUOS) },
          { value: TeamMode.CUSTOM, label: getTeamModeDisplayName(TeamMode.CUSTOM) },
        ], editable)
      );

      // Friendly Fire
      grid.appendChild(
        this._createToggleControl('friendlyFire', 'Friendly Fire', editable)
      );

      // Host Plays
      grid.appendChild(
        this._createToggleControl('hostPlays', 'Host Plays', editable)
      );
    }));

    // === MODE-SPECIFIC SETTINGS ===
    section.appendChild(this._createSettingsGroup('MODE SETTINGS', editable, (grid) => {
      // Lives (Survival)
      grid.appendChild(
        this._createNumberControl('lives', 'Lives', CONFIG_LIMITS.lives.min, CONFIG_LIMITS.lives.max, editable)
      );

      // Target Score (Score Race)
      grid.appendChild(
        this._createNumberControl('targetScore', 'Target Score', CONFIG_LIMITS.targetScore.min, CONFIG_LIMITS.targetScore.max, editable)
      );

      // Time Limit (Time Attack)
      grid.appendChild(
        this._createNumberControl('timeLimit', 'Time (sec)', CONFIG_LIMITS.timeLimit.min, CONFIG_LIMITS.timeLimit.max, editable)
      );

      // Crown Time (King of Worms)
      grid.appendChild(
        this._createNumberControl('crownTime', 'Crown Time', CONFIG_LIMITS.crownTime.min, CONFIG_LIMITS.crownTime.max, editable)
      );

      // Respawn Delay
      grid.appendChild(
        this._createNumberControl('respawnDelay', 'Respawn (sec)', CONFIG_LIMITS.respawnDelay.min, CONFIG_LIMITS.respawnDelay.max, editable)
      );
    }));

    // === PLAYERS SECTION ===
    section.appendChild(this._createSettingsGroup('PLAYERS', editable, (grid) => {
      // Max Players
      grid.appendChild(
        this._createNumberControl('maxPlayers', 'Max Players', CONFIG_LIMITS.maxPlayers.min, CONFIG_LIMITS.maxPlayers.max, editable)
      );

      // AI Worms
      grid.appendChild(
        this._createNumberControl('aiWorms', 'AI Worms', CONFIG_LIMITS.aiWorms.min, CONFIG_LIMITS.aiWorms.max, editable)
      );

      // AI Difficulty
      grid.appendChild(
        this._createSelectControl('aiDifficulty', 'AI Difficulty', [
          { value: 'easy' as AIDifficulty, label: getAIDifficultyDisplayName('easy') },
          { value: 'normal' as AIDifficulty, label: getAIDifficultyDisplayName('normal') },
          { value: 'hard' as AIDifficulty, label: getAIDifficultyDisplayName('hard') },
        ], editable)
      );
    }));

    // === MAP SECTION ===
    section.appendChild(this._createSettingsGroup('MAP', editable, (grid) => {
      // Map Shape
      grid.appendChild(
        this._createSelectControl('mapShape', 'Shape', [
          { value: MapShape.CIRCLE, label: 'Circle' },
          { value: MapShape.RECTANGLE, label: 'Rectangle' },
        ], editable)
      );

      // Map Size
      grid.appendChild(
        this._createNumberControl('mapSize', 'Size', CONFIG_LIMITS.mapSize.min, CONFIG_LIMITS.mapSize.max, editable)
      );

      // Shrink Speed
      grid.appendChild(
        this._createSelectControl('shrinkSpeed', 'Shrink', [
          { value: 'none' as ShrinkSpeed, label: 'None' },
          { value: 'slow' as ShrinkSpeed, label: 'Slow' },
          { value: 'normal' as ShrinkSpeed, label: 'Normal' },
          { value: 'fast' as ShrinkSpeed, label: 'Fast' },
        ], editable)
      );

      // Shrink Delay
      grid.appendChild(
        this._createNumberControl('shrinkDelay', 'Shrink Delay', CONFIG_LIMITS.shrinkDelay.min, CONFIG_LIMITS.shrinkDelay.max, editable)
      );
    }));

    // === GAMEPLAY SECTION ===
    section.appendChild(this._createSettingsGroup('GAMEPLAY', editable, (grid) => {
      // Food Density
      grid.appendChild(
        this._createSelectControl('foodDensity', 'Food', [
          { value: 'sparse' as FoodDensity, label: 'Sparse' },
          { value: 'normal' as FoodDensity, label: 'Normal' },
          { value: 'dense' as FoodDensity, label: 'Dense' },
        ], editable)
      );

      // Powerup Rate
      grid.appendChild(
        this._createSelectControl('powerupRatePreset', 'Powerups', [
          { value: 'scarce' as PowerupRatePresetId, label: getPowerupRateDisplayName('scarce') },
          { value: 'rare' as PowerupRatePresetId, label: getPowerupRateDisplayName('rare') },
          { value: 'normal' as PowerupRatePresetId, label: getPowerupRateDisplayName('normal') },
          { value: 'abundant' as PowerupRatePresetId, label: getPowerupRateDisplayName('abundant') },
          { value: 'plentiful' as PowerupRatePresetId, label: getPowerupRateDisplayName('plentiful') },
          { value: 'mayhem' as PowerupRatePresetId, label: getPowerupRateDisplayName('mayhem') },
        ], editable)
      );

      // Visibility
      grid.appendChild(
        this._createSelectControl('visibilityPreset', 'Visibility', [
          { value: 'full_vision' as VisibilityPresetId, label: getVisibilityDisplayName('full_vision') },
          { value: 'eagle_eye' as VisibilityPresetId, label: getVisibilityDisplayName('eagle_eye') },
          { value: 'normal' as VisibilityPresetId, label: getVisibilityDisplayName('normal') },
          { value: 'limited' as VisibilityPresetId, label: getVisibilityDisplayName('limited') },
          { value: 'dense_fog' as VisibilityPresetId, label: getVisibilityDisplayName('dense_fog') },
          { value: 'extreme_fog' as VisibilityPresetId, label: getVisibilityDisplayName('extreme_fog') },
        ], editable)
      );
    }));

    return section;
  }

  /**
   * Create a collapsible settings group
   */
  private _createSettingsGroup(
    title: string,
    _editable: boolean,
    addControls: (grid: HTMLDivElement) => void
  ): HTMLDivElement {
    const group = document.createElement('div');
    group.style.cssText = `
      background: ${COLORS.bgDarker};
      border-radius: 12px;
      border: 1px solid ${COLORS.border};
      overflow: hidden;
    `;

    // Header (clickable to expand/collapse)
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 14px;
      cursor: pointer;
      user-select: none;
      transition: background 0.2s ease;
    `;

    const label = document.createElement('span');
    label.textContent = title;
    label.style.cssText = `
      font-size: 10px;
      font-weight: 700;
      color: ${COLORS.textMuted};
      letter-spacing: 1.5px;
    `;
    header.appendChild(label);

    const arrow = document.createElement('span');
    arrow.textContent = '\u25BC';
    arrow.style.cssText = `
      font-size: 8px;
      color: ${COLORS.textMuted};
      transition: transform 0.2s ease;
    `;
    header.appendChild(arrow);

    // Content
    const content = document.createElement('div');
    content.style.cssText = `
      padding: 0 14px 14px 14px;
      transition: all 0.2s ease;
    `;

    const grid = document.createElement('div');
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    `;

    addControls(grid);
    content.appendChild(grid);

    // Toggle expand/collapse
    let isExpanded = true;
    header.addEventListener('click', () => {
      isExpanded = !isExpanded;
      content.style.display = isExpanded ? 'block' : 'none';
      arrow.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)';
      SoundManager.getInstance().play(SoundType.UI_CLICK);
    });

    header.addEventListener('mouseover', () => {
      header.style.background = 'rgba(255, 255, 255, 0.03)';
    });
    header.addEventListener('mouseout', () => {
      header.style.background = 'transparent';
    });

    group.appendChild(header);
    group.appendChild(content);

    return group;
  }

  /**
   * Create a toggle/checkbox control for settings
   */
  private _createToggleControl(
    key: string,
    label: string,
    editable: boolean
  ): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: rgba(0, 0, 0, 0.2);
      border: 1px solid ${COLORS.border};
      border-radius: 8px;
    `;

    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    labelEl.style.cssText = `
      font-size: 12px;
      font-weight: 500;
      color: ${COLORS.textSecondary};
    `;
    container.appendChild(labelEl);

    // Toggle switch
    const toggle = document.createElement('div');
    toggle.style.cssText = `
      width: 40px;
      height: 22px;
      border-radius: 11px;
      background: ${COLORS.border};
      position: relative;
      cursor: ${editable ? 'pointer' : 'not-allowed'};
      transition: background 0.2s ease;
    `;

    const knob = document.createElement('div');
    knob.style.cssText = `
      width: 18px;
      height: 18px;
      border-radius: 9px;
      background: white;
      position: absolute;
      top: 2px;
      left: 2px;
      transition: transform 0.2s ease;
    `;
    toggle.appendChild(knob);

    // Create a hidden checkbox for form handling
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.style.display = 'none';
    checkbox.disabled = !editable;

    // Set initial value
    const configValue = this._getConfigValue(this._config, key);
    checkbox.checked = Boolean(configValue);
    this._updateToggleVisual(toggle, knob, checkbox.checked);

    if (editable) {
      toggle.addEventListener('click', () => {
        checkbox.checked = !checkbox.checked;
        this._updateToggleVisual(toggle, knob, checkbox.checked);
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        this._handleConfigChange(key, checkbox.checked);
      });
    }

    this._configControls.set(key, checkbox);
    container.appendChild(toggle);

    return container;
  }

  /**
   * Update toggle visual state
   */
  private _updateToggleVisual(toggle: HTMLDivElement, knob: HTMLDivElement, checked: boolean): void {
    if (checked) {
      toggle.style.background = COLORS.primary;
      knob.style.transform = 'translateX(18px)';
    } else {
      toggle.style.background = COLORS.border;
      knob.style.transform = 'translateX(0)';
    }
  }

  /**
   * Create a select control for settings
   */
  private _createSelectControl(
    key: string,
    label: string,
    options: { value: string; label: string }[],
    editable: boolean
  ): HTMLDivElement {
    const container = document.createElement('div');

    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    labelEl.style.cssText = `
      display: block;
      font-size: 11px;
      font-weight: 600;
      color: ${COLORS.textMuted};
      margin-bottom: 4px;
    `;
    container.appendChild(labelEl);

    const select = document.createElement('select');
    select.disabled = !editable;
    select.style.cssText = `
      width: 100%;
      padding: 10px 12px;
      border: 1px solid ${COLORS.border};
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.2);
      color: ${COLORS.textPrimary};
      font-size: 13px;
      font-weight: 500;
      cursor: ${editable ? 'pointer' : 'not-allowed'};
      outline: none;
      transition: all 0.2s ease;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' fill-opacity='0.5' d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      padding-right: 32px;
    `;

    options.forEach((opt) => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label;
      option.style.background = '#1a1a2e';
      select.appendChild(option);
    });

    // Set initial value
    const configValue = this._getConfigValue(this._config, key);
    if (configValue !== undefined) {
      select.value = String(configValue);
    }

    if (editable) {
      select.addEventListener('change', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        this._handleConfigChange(key, select.value);
      });
      select.addEventListener('focus', () => {
        select.style.borderColor = COLORS.primary;
      });
      select.addEventListener('blur', () => {
        select.style.borderColor = COLORS.border;
      });
    }

    this._configControls.set(key, select);
    container.appendChild(select);

    return container;
  }

  /**
   * Create a number input control for settings
   */
  private _createNumberControl(
    key: string,
    label: string,
    min: number,
    max: number,
    editable: boolean
  ): HTMLDivElement {
    const container = document.createElement('div');

    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    labelEl.style.cssText = `
      display: block;
      font-size: 11px;
      font-weight: 600;
      color: ${COLORS.textMuted};
      margin-bottom: 4px;
    `;
    container.appendChild(labelEl);

    const inputContainer = document.createElement('div');
    inputContainer.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const input = document.createElement('input');
    input.type = 'number';
    input.min = String(min);
    input.max = String(max);
    input.disabled = !editable;
    input.style.cssText = `
      flex: 1;
      padding: 10px 12px;
      border: 1px solid ${COLORS.border};
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.2);
      color: ${COLORS.textPrimary};
      font-size: 13px;
      font-weight: 500;
      outline: none;
      transition: all 0.2s ease;
      text-align: center;
      -moz-appearance: textfield;
    `;

    // Set initial value
    const configValue = this._getConfigValue(this._config, key);
    if (configValue !== undefined) {
      input.value = String(configValue);
    }

    if (editable) {
      input.addEventListener('change', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        let value = parseInt(input.value, 10);
        if (isNaN(value)) value = min;
        value = Math.max(min, Math.min(max, value));
        input.value = String(value);
        this._handleConfigChange(key, value);
      });
      input.addEventListener('focus', () => {
        input.style.borderColor = COLORS.primary;
      });
      input.addEventListener('blur', () => {
        input.style.borderColor = COLORS.border;
      });
    }

    this._configControls.set(key, input);
    inputContainer.appendChild(input);

    container.appendChild(inputContainer);

    return container;
  }

  /**
   * Create the player list section
   */
  private _createPlayerListSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.style.cssText = `
      background: ${COLORS.bgDarker};
      border-radius: 16px;
      border: 1px solid ${COLORS.border};
      overflow: hidden;
    `;
    return section;
  }

  /**
   * Create a player entry in the list
   */
  private _createPlayerEntry(player: PlayerInfo): HTMLDivElement {
    const entry = document.createElement('div');
    entry.style.cssText = `
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 8px;
      transition: all 0.2s ease;
    `;

    // Player icon
    const icon = document.createElement('span');
    icon.textContent = player.isAI ? '[ AI ]' : player.isHost ? '[ You ]' : '';
    icon.style.cssText = `
      font-size: 10px;
      font-weight: 700;
      color: ${player.isAI ? COLORS.warning : COLORS.primary};
      padding: 3px 6px;
      background: ${player.isAI ? 'rgba(249, 237, 105, 0.15)' : 'rgba(78, 205, 196, 0.15)'};
      border-radius: 4px;
      min-width: 32px;
      text-align: center;
    `;
    if (icon.textContent) {
      entry.appendChild(icon);
    }

    // Player name
    const name = document.createElement('span');
    name.textContent = player.name;
    name.style.cssText = `
      flex: 1;
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.textPrimary};
    `;
    entry.appendChild(name);

    // Host badge
    if (player.isHost && !player.isAI) {
      const hostBadge = document.createElement('span');
      hostBadge.textContent = 'Host';
      hostBadge.style.cssText = `
        font-size: 10px;
        font-weight: 700;
        color: ${COLORS.primary};
        padding: 3px 8px;
        background: rgba(78, 205, 196, 0.15);
        border-radius: 4px;
      `;
      entry.appendChild(hostBadge);
    }

    // Ready status
    if (!player.isAI) {
      const status = document.createElement('span');
      status.textContent = player.isReady ? 'Ready' : 'Not Ready';
      status.style.cssText = `
        font-size: 11px;
        font-weight: 600;
        color: ${player.isReady ? COLORS.success : COLORS.textMuted};
      `;
      entry.appendChild(status);

      // Checkmark for ready
      if (player.isReady) {
        const check = document.createElement('span');
        check.textContent = String.fromCodePoint(0x2713);
        check.style.cssText = `
          font-size: 14px;
          color: ${COLORS.success};
        `;
        entry.appendChild(check);
      }
    }

    return entry;
  }

  /**
   * Create the host action buttons
   */
  private _createHostActionButtons(): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding-top: 16px;
      border-top: 1px solid ${COLORS.border};
    `;

    // Start Game button
    this._startButton = this._createPrimaryButton('Start Game', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._events.onStartGame();
    });
    this._startButton.disabled = true;
    this._startButton.style.opacity = '0.5';
    container.appendChild(this._startButton);

    // Back button
    const backButton = this._createSecondaryButton('Back', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._events.onBack();
    });
    container.appendChild(backButton);

    return container;
  }

  /**
   * Create a primary action button
   */
  private _createPrimaryButton(text: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText = `
      width: 100%;
      padding: 16px 24px;
      border: none;
      border-radius: 14px;
      background: linear-gradient(135deg, ${COLORS.primary} 0%, #2cb5e8 100%);
      color: ${COLORS.textPrimary};
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;
      box-shadow: 0 4px 20px rgba(78, 205, 196, 0.4);
      transition: all 0.2s ease;
    `;

    btn.addEventListener('click', onClick);
    btn.addEventListener('mouseover', () => {
      if (!btn.disabled) {
        btn.style.transform = 'translateY(-2px)';
        btn.style.boxShadow = '0 6px 25px rgba(78, 205, 196, 0.5)';
        SoundManager.getInstance().play(SoundType.UI_HOVER);
      }
    });
    btn.addEventListener('mouseout', () => {
      btn.style.transform = 'translateY(0)';
      btn.style.boxShadow = '0 4px 20px rgba(78, 205, 196, 0.4)';
    });

    return btn;
  }

  /**
   * Create a secondary action button
   */
  private _createSecondaryButton(text: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText = `
      padding: 12px 24px;
      border: 1px solid ${COLORS.border};
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.05);
      color: ${COLORS.textSecondary};
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
    `;

    btn.addEventListener('click', onClick);
    btn.addEventListener('mouseover', () => {
      btn.style.background = 'rgba(255, 255, 255, 0.1)';
      btn.style.borderColor = COLORS.borderHover;
      SoundManager.getInstance().play(SoundType.UI_HOVER);
    });
    btn.addEventListener('mouseout', () => {
      btn.style.background = 'rgba(255, 255, 255, 0.05)';
      btn.style.borderColor = COLORS.border;
    });

    return btn;
  }

  // ============================================================================
  // PRIVATE METHODS - EVENT HANDLERS
  // ============================================================================

  /**
   * Handle joining a game
   */
  private _handleJoin(): void {
    if (!this._roomCodeInput) return;
    const code = this._roomCodeInput.value.trim().toUpperCase();
    if (code.length >= 4) {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this._events.onJoinGame(code);
    }
  }

  /**
   * Handle configuration change
   */
  private _handleConfigChange(key: string, value: unknown): void {
    // Update local config using type-safe setter
    this._setConfigValue(key, value);

    // Handle shrink enabled based on shrink speed
    if (key === 'shrinkSpeed') {
      this._config.shrinkEnabled = value !== 'none';
    }

    // Mark as custom preset if config differs from selected preset
    this._updatePresetSelection();

    // Notify listeners
    this._events.onConfigChange(this._config);
  }

  /**
   * Select a preset
   */
  private _selectPreset(presetKey: string): void {
    this._selectedPreset = presetKey;

    if (presetKey !== 'custom') {
      const preset = PRESETS.find((p) => p.key === presetKey);
      if (preset) {
        this._config = { ...preset.config };
        this._updateConfigControls();
        this._events.onConfigChange(this._config);
      }
    }

    this._updatePresetButtonStyles();
  }

  /**
   * Update preset button styles
   */
  private _updatePresetButtonStyles(): void {
    this._presetButtons.forEach((btn, key) => {
      if (key === this._selectedPreset) {
        btn.style.background = `rgba(78, 205, 196, 0.2)`;
        btn.style.borderColor = COLORS.primary;
        btn.style.color = COLORS.primary;
      } else {
        btn.style.background = 'transparent';
        btn.style.borderColor = COLORS.border;
        btn.style.color = COLORS.textSecondary;
      }
    });
  }

  /**
   * Update preset selection based on current config
   */
  private _updatePresetSelection(): void {
    // Check if current config matches any preset
    let matchingPreset: string | null = null;

    for (const preset of PRESETS) {
      if (this._configMatchesPreset(preset.config)) {
        matchingPreset = preset.key;
        break;
      }
    }

    if (matchingPreset) {
      this._selectedPreset = matchingPreset;
    } else {
      this._selectedPreset = 'custom';
    }

    this._updatePresetButtonStyles();
  }

  /**
   * Check if current config matches a preset
   */
  private _configMatchesPreset(preset: LocalPartyConfig): boolean {
    const keysToCompare: (keyof LocalPartyConfig)[] = [
      'gameMode', 'teamMode', 'mapShape', 'mapSize', 'shrinkSpeed', 'shrinkDelay',
      'maxPlayers', 'aiWorms', 'aiDifficulty', 'lives', 'targetScore', 'timeLimit',
      'crownTime', 'friendlyFire', 'hostPlays', 'respawnDelay', 'foodDensity',
      'powerupRatePreset', 'visibilityPreset'
    ];

    return keysToCompare.every((key) => this._config[key] === preset[key]);
  }

  /**
   * Update config control values
   */
  private _updateConfigControls(): void {
    this._configControls.forEach((control, key) => {
      const value = this._getConfigValue(this._config, key);
      if (value !== undefined) {
        if (control instanceof HTMLSelectElement) {
          control.value = String(value);
        } else if (control instanceof HTMLInputElement) {
          if (control.type === 'checkbox') {
            control.checked = Boolean(value);
          } else {
            control.value = String(value);
          }
        }
      }
    });
  }

  /**
   * Update start button state based on player readiness
   */
  private _updateStartButtonState(players: PlayerInfo[]): void {
    if (!this._isHost || !this._startButton) return;

    const humanPlayers = players.filter((p) => !p.isAI);
    const allReady = humanPlayers.length > 0 && humanPlayers.every((p) => p.isReady);
    const hasEnoughPlayers = humanPlayers.length >= 1;

    this.setCanStart(allReady && hasEnoughPlayers);
  }

  /**
   * Type-safe getter for config values by key
   */
  private _getConfigValue(config: LocalPartyConfig, key: string): unknown {
    switch (key) {
      case 'gameMode': return config.gameMode;
      case 'teamMode': return config.teamMode;
      case 'mapShape': return config.mapShape;
      case 'mapSize': return config.mapSize;
      case 'shrinkEnabled': return config.shrinkEnabled;
      case 'shrinkSpeed': return config.shrinkSpeed;
      case 'shrinkDelay': return config.shrinkDelay;
      case 'maxPlayers': return config.maxPlayers;
      case 'aiWorms': return config.aiWorms;
      case 'aiDifficulty': return config.aiDifficulty;
      case 'lives': return config.lives;
      case 'targetScore': return config.targetScore;
      case 'timeLimit': return config.timeLimit;
      case 'crownTime': return config.crownTime;
      case 'friendlyFire': return config.friendlyFire;
      case 'hostPlays': return config.hostPlays;
      case 'respawnDelay': return config.respawnDelay;
      case 'foodDensity': return config.foodDensity;
      case 'powerupRatePreset': return config.powerupRatePreset;
      case 'visibilityPreset': return config.visibilityPreset;
      default: return undefined;
    }
  }

  /**
   * Type-safe setter for config values by key
   */
  private _setConfigValue(key: string, value: unknown): void {
    switch (key) {
      case 'gameMode': this._config.gameMode = value as LocalGameMode; break;
      case 'teamMode': this._config.teamMode = value as TeamMode; break;
      case 'mapShape': this._config.mapShape = value as MapShape; break;
      case 'mapSize': this._config.mapSize = value as number; break;
      case 'shrinkEnabled': this._config.shrinkEnabled = value as boolean; break;
      case 'shrinkSpeed': this._config.shrinkSpeed = value as ShrinkSpeed; break;
      case 'shrinkDelay': this._config.shrinkDelay = value as number; break;
      case 'maxPlayers': this._config.maxPlayers = value as number; break;
      case 'aiWorms': this._config.aiWorms = value as number; break;
      case 'aiDifficulty': this._config.aiDifficulty = value as AIDifficulty; break;
      case 'lives': this._config.lives = value as number; break;
      case 'targetScore': this._config.targetScore = value as number; break;
      case 'timeLimit': this._config.timeLimit = value as number; break;
      case 'crownTime': this._config.crownTime = value as number; break;
      case 'friendlyFire': this._config.friendlyFire = value as boolean; break;
      case 'hostPlays': this._config.hostPlays = value as boolean; break;
      case 'respawnDelay': this._config.respawnDelay = value as number; break;
      case 'foodDensity': this._config.foodDensity = value as FoodDensity; break;
      case 'powerupRatePreset': this._config.powerupRatePreset = value as PowerupRatePresetId; break;
      case 'visibilityPreset': this._config.visibilityPreset = value as VisibilityPresetId; break;
    }
  }
}
